import React from 'react';
import { Link } from 'react-router-dom';
import { Card, Tag, Typography } from 'antd';
import {
  ApartmentOutlined,
  ArrowRightOutlined,
  LineChartOutlined,
  SwapOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import BrainIcon from '../components/BrainIcon';
import EmptyState from '../components/EmptyState';
import LoadingSkeleton from '../components/LoadingSkeleton';
import { useAnalyticsIndex } from '../hooks/useAnalytics';
import './IntelligenceLanding.css';

const { Title, Paragraph, Text } = Typography;

const workbenchModules = [
  {
    key: 'league-trends',
    title: 'League trends',
    description: 'Chart how scoring, positional advantage, roster age, and competitive balance change over time.',
    icon: <LineChartOutlined />,
    inputs: ['Season scoring', 'Position ranks', 'Youth vs veterans'],
  },
  {
    key: 'franchise-comparison',
    title: 'Franchise comparison',
    description: 'Compare keeper value, draft efficiency, waiver activity, lineup behavior, and sustained success.',
    icon: <TeamOutlined />,
    inputs: ['Draft outcomes', 'Keeper starts', 'Weekly decisions'],
  },
  {
    key: 'transaction-network',
    title: 'Transaction network',
    description: 'Explore trade partners, move timing, roster churn, and which relationships produced lasting value.',
    icon: <SwapOutlined />,
    inputs: ['Trade groups', 'Adds and drops', 'Roster snapshots'],
  },
];

const IntelligenceLanding: React.FC = () => {
  const indexQuery = useAnalyticsIndex();

  if (indexQuery.isLoading) {
    return (
      <div className="intelligence-page">
        <LoadingSkeleton height="270px" />
        <div className="intelligence-loading-grid">
          {[...Array(3)].map((_, index) => <LoadingSkeleton key={index} height="210px" />)}
        </div>
      </div>
    );
  }

  if (indexQuery.error) {
    return (
      <div className="intelligence-page">
        <Title level={1}>League Intelligence</Title>
        <EmptyState
          message="Failed to load intelligence"
          description="Regenerate the local analytics export and try again."
        />
      </div>
    );
  }

  const caseStudies = indexQuery.data?.caseStudies ?? [];

  return (
    <div className="intelligence-page">
      <header className="intelligence-hero">
        <div className="intelligence-hero-icon" aria-hidden="true">
          <BrainIcon />
        </div>
        <div className="intelligence-hero-copy">
          <div className="intelligence-kicker">FantasyArchive analysis</div>
          <Title level={1}>League Intelligence</Title>
          <Paragraph>
            Long-form case studies and explainable analytics built from the full league archive—designed
            to separate skill, decisions, roster construction, and plain old fantasy luck.
          </Paragraph>
          <div className="intelligence-hero-tags" aria-label="Intelligence capabilities">
            <Tag>SQL-backed</Tag>
            <Tag>Evidence included</Tag>
            <Tag>Methodology versioned</Tag>
          </div>
        </div>
      </header>

      <section className="intelligence-section" aria-labelledby="case-studies-title">
        <div className="intelligence-section-heading">
          <div>
            <span className="intelligence-section-eyebrow">Deep dives</span>
            <Title id="case-studies-title" level={2}>Case studies</Title>
            <Paragraph>
              Franchise stories tested against weekly matchups, league-relative baselines, and explicit counterfactuals.
            </Paragraph>
          </div>
          <span className="intelligence-section-count">
            {caseStudies.length} published
          </span>
        </div>

        {caseStudies.length === 0 ? (
          <EmptyState
            message="No case studies yet"
            description="Run the local analytics exporter to publish the first study."
          />
        ) : (
          <div className="intelligence-case-grid">
            {caseStudies.map((caseStudy, index) => (
              <Link
                key={caseStudy.slug}
                to={`/analytics/case-studies/${caseStudy.slug}`}
                className="intelligence-case-link"
              >
                <Card className="intelligence-case-card" bordered={false}>
                  <div className="intelligence-case-meta">
                    <span>Case study {String(index + 1).padStart(2, '0')}</span>
                    <span>{caseStudy.startYear}–{caseStudy.endYear}</span>
                  </div>
                  <Title level={3}>{caseStudy.title}</Title>
                  <Text className="intelligence-case-subject">{caseStudy.subject}</Text>
                  <Paragraph>{caseStudy.headline}</Paragraph>
                  <span className="intelligence-case-action">
                    Read the evidence <ArrowRightOutlined />
                  </span>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="intelligence-section intelligence-workbench" aria-labelledby="workbench-title">
        <div className="intelligence-section-heading">
          <div>
            <span className="intelligence-section-eyebrow">Next layer</span>
            <Title id="workbench-title" level={2}>Analytics workbench</Title>
            <Paragraph>
              Reserved for interactive charts and comparisons built from public-safe derived data.
              These modules are planned surfaces, not unpublished findings.
            </Paragraph>
          </div>
          <Tag color="gold">In development</Tag>
        </div>

        <div className="intelligence-workbench-layout">
          <div className="intelligence-module-grid">
            {workbenchModules.map(module => (
              <Card key={module.key} className="intelligence-module-card" size="small">
                <div className="intelligence-module-heading">
                  <span className="intelligence-module-icon">{module.icon}</span>
                  <Tag>Planned</Tag>
                </div>
                <Title level={3}>{module.title}</Title>
                <Paragraph>{module.description}</Paragraph>
                <div className="intelligence-data-tags">
                  {module.inputs.map(input => <span key={input}>{input}</span>)}
                </div>
              </Card>
            ))}
          </div>

          <div className="intelligence-chart-reserve">
            <div className="intelligence-chart-title">
              <ApartmentOutlined />
              <span>Future chart canvas</span>
            </div>
            <div className="intelligence-chart-frame" role="img" aria-label="Reserved area for future interactive analytics charts">
              <span className="intelligence-chart-axis intelligence-chart-axis--y">Metric</span>
              <div className="intelligence-chart-gridlines" aria-hidden="true">
                <i /><i /><i /><i />
              </div>
              <span className="intelligence-chart-axis intelligence-chart-axis--x">Season or franchise</span>
              <div className="intelligence-chart-empty">
                Select a future module to explore the archive
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IntelligenceLanding;
